import React, { useState, useEffect } from 'react';
import authService from '../../utils/services/authService';
import snipeLogo from '../../assets/snipe_logo.png';
import aflLogo from '../../assets/afl_logo.png';
import nbaLogo from '../../assets/nba_logo.png';

function RegisterModal({ isOpen, onClose, selectedPlan, onSwitchToLogin }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const getSportLogo = (sport) => {
        const logos = {
            'NBA': nbaLogo,
            'AFL': aflLogo,
            'ALL': null
        };
        return logos[sport];
    };

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setEmail('');
            setPassword('');
            setConfirmPassword('');
            setTermsAccepted(false);
            setError('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    // Check password strength
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    const isPasswordStrong = passwordRegex.test(password);

    // Check if form is valid
    const isFormValid = email && password && confirmPassword && termsAccepted && password === confirmPassword && isPasswordStrong;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validate passwords match
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        // Validate password strength
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
        if (!passwordRegex.test(password)) {
            setError('Password must be at least 8 characters with 1 uppercase, 1 lowercase, and 1 number');
            return;
        }

        // Validate selected plan
        if (!selectedPlan) {
            setError('Please select a subscription plan');
            return;
        }

        setIsLoading(true);

        try {
            // Call registration API
            const result = await authService.register(
                email,
                password,
                selectedPlan.tier,
                selectedPlan.sport
            );

            if (result.success && result.data.checkout_url) {
                // Redirect to Stripe Checkout
                window.location.href = result.data.checkout_url;
            } else {
                // Handle error response
                setError(result.message || 'Registration failed. Please try again.');
                setIsLoading(false);
            }
        } catch (error) {
            console.error('Registration error:', error);
            setError('An unexpected error occurred. Please try again.');
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            ></div>

            {/* Modal Content */}
            <div className="relative w-full max-w-md bg-gradient-to-br from-[#0f172a] to-[#020617] rounded-2xl border border-slate-800/50 shadow-2xl p-8 animate-modal-in max-h-[95vh] overflow-y-auto">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors duration-200"
                    aria-label="Close modal"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Logo */}
                <div className="flex justify-center -mt-8 -mb-2">
                    <img src={snipeLogo} alt="Snipe Logo" className="h-32 w-auto object-contain" />
                </div>

                <h2 className="text-2xl font-bold text-center mb-2">Create Your Account</h2>
                <p className="text-slate-400 text-center text-sm mb-6">Sign up to get started with your subscription</p>

                {/* Registration Form */}
                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 flex items-center">
                            <svg className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-sm text-red-400">{error}</p>
                        </div>
                    )}

                    {/* Email Input */}
                    <div>
                        <label htmlFor="register-email" className="block text-sm font-medium text-slate-300 mb-2">
                            Email Address
                        </label>
                        <input
                            type="email"
                            id="register-email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                // Clear error when user types
                                if (error) setError('');
                            }}
                            className="w-full px-4 py-3 bg-[#0a0f1e]/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition duration-200"
                            placeholder="you@example.com"
                            required
                        />
                    </div>

                    {/* Password Input */}
                    <div>
                        <label htmlFor="register-password" className="block text-sm font-medium text-slate-300 mb-2">
                            Password
                        </label>
                        <input
                            type="password"
                            id="register-password"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                // Clear error when user updates password
                                if (error === 'Passwords do not match' && confirmPassword && e.target.value === confirmPassword) {
                                    setError('');
                                }
                            }}
                            className="w-full px-4 py-3 bg-[#0a0f1e]/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition duration-200"
                            placeholder="••••••••"
                            required
                        />
                        <p className={`text-xs mt-1 ${password && !isPasswordStrong ? 'text-red-400' : 'text-slate-500'}`}>
                            At least 8 characters with 1 uppercase, 1 lowercase, and 1 number
                        </p>
                    </div>

                    {/* Confirm Password Input */}
                    <div>
                        <label htmlFor="register-confirm-password" className="block text-sm font-medium text-slate-300 mb-2">
                            Confirm Password
                        </label>
                        <input
                            type="password"
                            id="register-confirm-password"
                            value={confirmPassword}
                            onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                // Show alert if passwords don't match and user has typed something
                                if (e.target.value && password && e.target.value !== password) {
                                    setError('Passwords do not match');
                                } else {
                                    setError('');
                                }
                            }}
                            className="w-full px-4 py-3 bg-[#0a0f1e]/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition duration-200"
                            placeholder="••••••••"
                            required
                        />
                    </div>

                    {/* Terms & Conditions */}
                    <div className="flex items-start">
                        <input
                            type="checkbox"
                            id="terms"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            required
                            className="w-4 h-4 mt-1 rounded border-slate-700 bg-[#0a0f1e]/50 text-emerald-500 focus:ring-2 focus:ring-emerald-500/50"
                        />
                        <label htmlFor="terms" className="ml-2 text-sm text-slate-400">
                            I agree to the{' '}
                            <a href="#terms" className="text-emerald-400 hover:text-emerald-300">
                                Terms of Service
                            </a>
                            {' '}and{' '}
                            <a href="#privacy" className="text-emerald-400 hover:text-emerald-300">
                                Privacy Policy
                            </a>
                        </label>
                    </div>

                    {/* Selected Plan Summary */}
                    {selectedPlan && (
                        <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-3 flex items-center justify-between">
                            {/* Sport Logo & Name */}
                            <div className="flex items-center gap-3">
                                {getSportLogo(selectedPlan.sport) && (
                                    <div className="w-8 h-8 bg-white rounded flex items-center justify-center p-0.5">
                                        <img 
                                            src={getSportLogo(selectedPlan.sport)} 
                                            alt={`${selectedPlan.sport} Logo`}
                                            className="w-full h-full object-contain"
                                        />
                                    </div>
                                )}
                                <div>
                                    <p className="text-sm font-semibold text-white">
                                        {selectedPlan.display_name || `${selectedPlan.sport} ${selectedPlan.tier}`}
                                    </p>
                                    {selectedPlan.trial_days > 0 && (
                                        <p className="text-xs text-emerald-400">
                                            {selectedPlan.trial_days}-day free trial
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Price */}
                            <div className="text-right">
                                <p className="text-lg font-bold text-emerald-400">
                                    ${selectedPlan.monthly_price}
                                    <span className="text-xs text-slate-400">/mo</span>
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Stripe Security Notice */}
                    <div className="flex items-center justify-center gap-2 text-xs text-slate-400 bg-slate-800/20 rounded-lg p-3 border border-slate-700/30">
                        <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <p className="text-center">
                            All payments are securely processed by <span className="text-white font-semibold">Stripe</span>. You will be redirected to Stripe for secure checkout.
                        </p>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isLoading || !isFormValid}
                        className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-3 rounded-lg font-semibold text-white hover:from-emerald-700 hover:to-teal-700 transition duration-200 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/40 hover:scale-[1.02] transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center"
                    >
                        {isLoading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Creating Account...
                            </>
                        ) : (
                            'Continue to checkout'
                        )}
                    </button>
                </form>

                {/* Sign In Link */}
                <div className="mt-6 text-center">
                    <p className="text-sm text-slate-400">
                        Already have an account?{' '}
                        <button 
                            onClick={() => {
                                onClose();
                                if (onSwitchToLogin) onSwitchToLogin();
                            }}
                            className="text-emerald-400 hover:text-emerald-300 font-semibold transition-colors duration-200"
                        >
                            Sign in
                        </button>
                    </p>
                </div>
            </div>

            <style jsx>{`
                @keyframes modal-in {
                    from {
                        opacity: 0;
                        transform: scale(0.95) translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }
                .animate-modal-in {
                    animation: modal-in 0.2s ease-out forwards;
                }
            `}</style>
        </div>
    );
}

export default RegisterModal;

